import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useLocation, useParams } from "react-router-dom";

import classes from './WorkQueuesControls.module.css'
import { retrieveSpecificWorkQ } from '../../../lib/systemData';
import { filterActions } from '../../../store/filter';
import { workqueueActions } from '../../../store/workqueue';
import { getPersistedWorkQSearchParams, computeWorkQDefaultFiltersFromConditions, applySavedWorkQueueView, hasPersistedWorkQFilterChoice, shouldDeferWorkQListForSavedFilters } from '../../../lib/filters';
import { SaveWorkQueueViewButton } from '../filters/SavedViewBanner';
import { getItemFromStorageSimple } from '../../../lib/script';
import MegaFilterContainer from '../megaFilters/MegaFilterContainer';
import FilterSearchContainer from '../megaFilters/searchbox/FilterSearchContainer';
import Exceptions from '../exceptions/Exceptions';
import GDQWorkQFilters from '../pages/custom/GDQWorkQFilters';
import RefreshWorkQApi from './RefreshWorkQApi';
import WorkQComponentBlock from '../pages/WorkQComponentBlock';
import PoliceReportsWorkQFitlers from '../pages/custom/policeReport/PoliceReportsWorkQFitlers';
import InvalidFaxWorkQFilters from '../pages/custom/invalidFax/InvalidFaxWorkQFilters';
import WorkQTotals from '../pages/totals/WorkQTotals';
import ExportWorkQ from '../pages/export/ExportWorkQ';
import WorkQHamburgerMenu from './WorkQHamburgerMenu';
import GDQReportExport from '../pages/gdqReport/GDQReportExport';
import CheckSetQueue from './CheckSetQueue';
import { missedApptsFollowUpWorkQSlugs } from '../pages/custom/globalWorkQStructure';
import MissedApptsFUFilters from '../pages/custom/followup/MissedApptsFUFilters';
import { resolveWorkQueueSlugFromRoute } from '../../../lib/workQueueNavigation';

const noopMainSelectAction = () => {}

export default function WorkQueuesControls() {
    const dispatch = useDispatch();
    const location = useLocation();
    const { workqSlug: workqSlugParam } = useParams();
    // Custom queues such as invalid-fax-q are static routes, so :workqSlug is absent.
    const workqSlug = useMemo(() => {
        return resolveWorkQueueSlugFromRoute({
            pathname: location.pathname,
            workqSlug: workqSlugParam,
        }) || 'police-report'
    }, [location.pathname, workqSlugParam]);
    const settingsRevision = useSelector((state) => state.system.settingsRevision);
    const filters = useSelector((state) => state.filter.filters);
    const savedViewsLoaded = useSelector((state) => state.filter.savedViewsLoaded);
    const savedView = useSelector((state) => (
        workqSlug ? state.filter.savedViewsBySlug?.[workqSlug] : null
    ));

    const workQ = useMemo(() => {
        return retrieveSpecificWorkQ(workqSlug)
    }, [workqSlug, settingsRevision]);

    useLayoutEffect(() => {
        if (!workqSlug) {
            return
        }

        const persisted = getPersistedWorkQSearchParams(workqSlug)
        if (persisted && Object.keys(persisted).length) {
            dispatch(filterActions.setSearchParams(persisted))
        } else {
            dispatch(filterActions.setSearchParams({}))
        }
        dispatch(workqueueActions.setInitialLoad(null))
    }, [workqSlug, dispatch])

    useEffect(() => {
        dispatch(filterActions.setOrderFilters(null))
        dispatch(filterActions.setCurrentPage(1))
    }, [workqSlug, dispatch])

    // Re-apply quick-filter defaults when queue config arrives after the first load.
    useEffect(() => {
        if (!workqSlug || !workQ?.filter_conditions) {
            return
        }

        if (shouldDeferWorkQListForSavedFilters(workqSlug, savedViewsLoaded)) {
            return
        }

        if (hasPersistedWorkQFilterChoice(workqSlug) || savedView) {
            return
        }

        const quickFilterKeys = Array.isArray(workQ.quick_filters) ? workQ.quick_filters : []
        if (!quickFilterKeys.length) {
            return
        }

        const userLocations = getItemFromStorageSimple('userLocations')
        const defaults = computeWorkQDefaultFiltersFromConditions(workQ.filter_conditions, userLocations)

        const missingQuickDefaults = quickFilterKeys.some((key) => {
            const defaultVal = defaults[key]
            if (defaultVal == null) {
                return false
            }
            const currentVal = filters?.[key]
            return currentVal == null || currentVal === ''
        })

        if (!missingQuickDefaults) {
            return
        }

        dispatch(filterActions.setFiltersMultiple({ ...defaults, ...(filters || {}) }))
        dispatch(workqueueActions.setInitialLoad(null))
        if (typeof window !== 'undefined') {
            window.__workQSettingsReinitSlug = workqSlug
        }
        dispatch(workqueueActions.setRefreshApi())
    }, [workqSlug, settingsRevision, workQ?.filter_conditions, workQ?.quick_filters, filters, savedView, savedViewsLoaded, dispatch])

    // If saved views arrive after the first list request, apply them when this slug has no session filters.
    useEffect(() => {
        if (!workqSlug || !workQ || !savedView) {
            return
        }
        if (hasPersistedWorkQFilterChoice(workqSlug)) {
            return
        }
        applySavedWorkQueueView(workqSlug, savedView, workQ, dispatch)
        dispatch(workqueueActions.setInitialLoad(null))
        dispatch(workqueueActions.setRefreshApi())
    }, [workqSlug, workQ, savedView, dispatch])

    return <div className={classes.controls_container}>
        {/* <CheckSetQueue /> */}
        <WorkQComponentBlock workqSlug={workqSlug}/>
        {/* {workQ && !workQ.type && <MasterFiltersMultiple workQ={workQ}/>} */}
        {workQ && workQ.type && workQ.slug != 'drivers-license-mismatch' && <SaveWorkQueueViewButton workQ={workQ}/>}
        {workQ && !workQ.type && <MegaFilterContainer key={workqSlug} workQ={workQ}/>}
        {workQ && !workQ.type && <Exceptions workQ={workQ}/>}

        {workQ && workqSlug && missedApptsFollowUpWorkQSlugs.includes(workqSlug) && (
            <>
                <FilterSearchContainer mainSelectAction={noopMainSelectAction} workQ={workQ} showGlobalSearch={false} />
                <MissedApptsFUFilters workQ={workQ} />
            </>
        )}

        {workqSlug == 'work-queues-global' && <GDQWorkQFilters workQ={workQ}/>}
        {workQ && workQ.slug == 'police-report' && <PoliceReportsWorkQFitlers workQ={workQ}/>}
        {workQ && workQ.slug == 'invalid-fax-q' && <InvalidFaxWorkQFilters workQ={workQ}/>}

        {/* refresh workq api */}
        <RefreshWorkQApi workQ={workQ}/>

        {workQ && !workQ.type && <>
            <WorkQTotals workq={workQ}/>
            <ExportWorkQ workq={workQ}/>
            {workqSlug == 'global-discharge-q' && <GDQReportExport workq={workQ}/>}
        </>}

        {/* hamburger alternative: uncomment this to use the hamburger menu instead of the buttons */}
        {/* {workQ && !workQ.type && <WorkQHamburgerMenu workQ={workQ}/>} */}
        
    </div>
}
